import { Suspense, lazy } from "react";
import { Provider } from "react-redux";
import { Redirect, Route, Router, Switch } from "react-router-dom";

import { SnackbarProvider } from "notistack";

import { ConnectedRouter } from "connected-react-router";
import { createBrowserHistory } from "history";
import { createRoot } from "react-dom/client";

import analyticsManager from "./analytics";
import AssignCarrierPopup from "./components/AssignCarrierPopup";
import Error403 from "./components/Error403Modal";
import ErrorBoundary from "./components/ErrorBoundary";
import { api } from "./config/variables";
import { useUtm } from "./hooks";
import useCobrowse from "./hooks/useCobrowse";
import ScrollToTop from "./ScrollTop";
import newRelicService from "./services/newRelicService";
import store from "./store/storeInstance";
import CargoFormRedirect from "./utils/CargoFormRedirect";
import ratingPopupManager from "./utils/ratingPopupManager";
import { registerServiceWorker } from "./utils/serviceWorkerRegistration";
import SuspenseFallback from "./utils/suspenseFallback";
import userSessionManager from "./utils/userSessionManager";

import "core-js/stable";
import "regenerator-runtime/runtime";
import "./styles/__global.scss";
import "./i18n";

const DepotConnect = lazy(() => import("./layouts/DepotConnect"));
const DepotConnectV2 = lazy(() => import("./layouts/DepotConnectV2"));
const AuthLayout = lazy(() => import("./layouts/Auth"));
const AdminLayout = lazy(() => import("./layouts/Admin"));
const ClientLayout = lazy(() => import("./layouts/Client"));
const SettingsLayout = lazy(() => import("./layouts/Settings"));
const PrivateRoute = lazy(() => import("./Authorization"));
const AboutLayout = lazy(() => import("./layouts/About"));
const PublicLayout = lazy(() => import("./layouts/Public"));

const hist = createBrowserHistory({ basename: "/" });

ratingPopupManager.initializeRatingCookie();
userSessionManager.initializeSessionStorage();

if (api.currentEnv === "production" && api.isDev === false) {
	newRelicService.initialize(api.newRelic);
	analyticsManager.initialize();
}
registerServiceWorker();

const RootIndex = () => {
	useUtm();
	useCobrowse();

	return (
		<ErrorBoundary>
			<Provider store={store}>
				<SnackbarProvider
					maxSnack={3}
					preventDuplicate
					anchorOrigin={{
						vertical: "top",
						horizontal: "right",
					}}
				>
					<ConnectedRouter history={hist}>
						<Router history={hist}>
							<Suspense fallback={<SuspenseFallback />}>
								<Error403 />
								<AssignCarrierPopup />
								<CargoFormRedirect />

								<ScrollToTop />
								<Switch>
									<Route path="/auth" component={AuthLayout} />
									<Route path="/admin" component={AdminLayout} />
									<Route path="/about" component={AboutLayout} />
									<Route path="/p" component={PublicLayout} />
									<Route path="/depot-connect-v2" component={DepotConnectV2} />
									<PrivateRoute path="/depot-connect">
										<DepotConnect />
									</PrivateRoute>
									<PrivateRoute path="/client">
										<ClientLayout />
									</PrivateRoute>
									<PrivateRoute path="/settings">
										<SettingsLayout />
									</PrivateRoute>
									<Redirect from="/" to="/client/dashboard" />
								</Switch>
							</Suspense>
						</Router>
					</ConnectedRouter>
				</SnackbarProvider>
			</Provider>
		</ErrorBoundary>
	);
};

const container = document.getElementById("root");
if (!container) {
	throw new Error("Root container missing: #root not found in DOM.");
}
const root = createRoot(container);
root.render(<RootIndex />);
